import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import { mdiFile } from '@mdi/js';
import { FieldValues, useController } from 'react-hook-form';

import { IFileFragment } from 'graphql/types';
import { FieldLabel } from 'common/components/FieldLabel';
import { BaseUploader } from 'common/components/BaseUploader';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
    id: string;
    helperText?: string;
    label?: string;
    maxUploadSize: number;
    acceptedFileTypes?: string;
    displayFile?: IFileFragment | null;
    onFileUpload?: (file: IFileFragment | null) => void;
}

export const FileField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
    id,
    acceptedFileTypes,
    helperText,
    label,
    maxUploadSize,
    displayFile,
    onFileUpload,
}: IProps & TControllerProps<TFieldValues>) => {
    const [uploadedFile, setUploadedFile] = useState<IFileFragment | null>(
        null
    );

    const control = asTypedControl<string | IFileFragment | undefined>(
        name,
        formControl
    );

    const {
        field,
        fieldState: { error },
    } = useController({ control, name });

    const errorMessage = error?.message;

    // Reset uploaded file when field value changes externally
    useEffect(() => {
        if (uploadedFile && uploadedFile.id !== field.value) {
            setUploadedFile(null);
        }

        // If field value is null/undefined, clear uploaded file
        if (!field.value && uploadedFile) setUploadedFile(null);
    }, [field.value, uploadedFile]);

    const handleChange = (newValue: IFileFragment | null) => {
        field.onChange(newValue?.id);

        setUploadedFile(newValue);

        // Notify parent component of the upload
        onFileUpload?.(newValue);
    };

    const displayFileValue = uploadedFile || displayFile || undefined;

    return (
        <Box sx={{ position: 'relative' }}>
            {!!label && (
                <FieldLabel>
                    <Typography>{label}</Typography>
                </FieldLabel>
            )}

            <BaseUploader
                acceptedFileTypes={acceptedFileTypes}
                file={displayFileValue}
                iconPath={mdiFile}
                id={id}
                maxFileSize={maxUploadSize}
                onUpload={handleChange}
            />

            {!!helperText && <FormHelperText>{helperText}</FormHelperText>}

            {!!errorMessage && (
                <FormHelperText error>{errorMessage}</FormHelperText>
            )}
        </Box>
    );
};
